import { CalendarEvent, CalendarCategory } from '../../services/calendarService';

export const ACCENT = '#3fced1';

export const MONTH_NAMES_DE = [
  'Januar', 'Februar', 'März', 'April', 'Mai', 'Juni',
  'Juli', 'August', 'September', 'Oktober', 'November', 'Dezember',
];

export const MONTH_SHORT_DE = [
  'Jan', 'Feb', 'Mär', 'Apr', 'Mai', 'Jun',
  'Jul', 'Aug', 'Sep', 'Okt', 'Nov', 'Dez',
];

export function startOfDayTs(ts: number): number {
  const d = new Date(ts);
  d.setHours(0, 0, 0, 0);
  return d.getTime();
}

export function endOfDayTs(ts: number): number {
  const d = new Date(ts);
  d.setHours(23, 59, 59, 999);
  return d.getTime();
}

export type EffectiveStatus = 'scheduled' | 'ongoing' | 'completed' | 'cancelled';

/**
 * Leitet den effektiven Status eines Events aus Status-Feld + Datumsfeldern ab.
 * Alles nullable-sicher.
 */
export function getEffectiveStatus(event: CalendarEvent, now: number = Date.now()): EffectiveStatus {
  if (event.status === 'cancelled') return 'cancelled';
  if (event.startDate == null) {
    return event.status === 'completed' ? 'completed' : 'scheduled';
  }
  const start = startOfDayTs(event.startDate);
  const end = endOfDayTs(event.endDate ?? event.startDate);
  if (now > end) return 'completed';
  if (now >= start) return 'ongoing';
  return 'scheduled';
}

// Sonderfälle für Darts-übliche Nicht-ISO-Codes (England, Schottland, Wales)
const FLAG_OVERRIDES: Record<string, string> = {
  EN: '\u{1F3F4}\u{E0067}\u{E0062}\u{E0065}\u{E006E}\u{E0067}\u{E007F}',
  ENG: '\u{1F3F4}\u{E0067}\u{E0062}\u{E0065}\u{E006E}\u{E0067}\u{E007F}',
  SCO: '\u{1F3F4}\u{E0067}\u{E0062}\u{E0073}\u{E0063}\u{E0074}\u{E007F}',
  SCT: '\u{1F3F4}\u{E0067}\u{E0062}\u{E0073}\u{E0063}\u{E0074}\u{E007F}',
  WAL: '\u{1F3F4}\u{E0067}\u{E0062}\u{E0077}\u{E006C}\u{E0073}\u{E007F}',
  WLS: '\u{1F3F4}\u{E0067}\u{E0062}\u{E0077}\u{E006C}\u{E0073}\u{E007F}',
  UK: '\u{1F1EC}\u{1F1E7}',
};

/** ISO-Alpha-2-Code -> Flaggen-Emoji (null wenn nicht ableitbar). */
export function flagEmoji(code?: string | null): string | null {
  if (!code) return null;
  const c = code.trim().toUpperCase();
  if (FLAG_OVERRIDES[c]) return FLAG_OVERRIDES[c];
  if (!/^[A-Z]{2}$/.test(c)) return null;
  return String.fromCodePoint(
    0x1f1e6 + c.charCodeAt(0) - 65,
    0x1f1e6 + c.charCodeAt(1) - 65,
  );
}

export function monthKeyOf(ts: number): string {
  const d = new Date(ts);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

export function monthLabelOf(key: string): string {
  const [yearStr, monthStr] = key.split('-');
  const monthIdx = parseInt(monthStr, 10) - 1;
  const name = MONTH_NAMES_DE[monthIdx] ?? '';
  return `${name} ${yearStr}`;
}

/** Stabiler React-Key für ein Event. */
export function eventKey(event: CalendarEvent): string {
  return event.id || event._id || `${event.title ?? 'event'}-${event.startDate ?? 0}`;
}

/** Dedupliziert Events anhand des eventKey (erste Instanz gewinnt). */
export function dedupeEvents(events: CalendarEvent[]): CalendarEvent[] {
  const seen = new Set<string>();
  const result: CalendarEvent[] = [];
  events.forEach((e) => {
    const key = eventKey(e);
    if (seen.has(key)) return;
    seen.add(key);
    result.push(e);
  });
  return result;
}

/** Preisgeld-Anzeige (nullable-sicher). */
export function formatPrize(event: CalendarEvent): string | null {
  if (event.prizeMoneyFormatted && event.prizeMoneyFormatted.trim().length > 0) {
    return event.prizeMoneyFormatted;
  }
  if (event.prizeMoney != null) {
    return `${event.prizeMoneyUnit || ''}${event.prizeMoney.toLocaleString('de-DE')}`;
  }
  return null;
}

/** Kompaktes Datum-Badge: Tag(e) + Monat, Multi-Day-fähig. */
export function dateBadgeParts(event: CalendarEvent): { top: string; bottom: string } | null {
  if (event.startDate == null) return null;
  const start = new Date(event.startDate);
  const end = event.endDate != null ? new Date(event.endDate) : null;
  const startMonth = MONTH_SHORT_DE[start.getMonth()] ?? '';

  if (
    end
    && (end.getDate() !== start.getDate()
      || end.getMonth() !== start.getMonth()
      || end.getFullYear() !== start.getFullYear())
  ) {
    const endMonth = MONTH_SHORT_DE[end.getMonth()] ?? '';
    if (end.getMonth() === start.getMonth() && end.getFullYear() === start.getFullYear()) {
      return { top: `${start.getDate()}–${end.getDate()}`, bottom: startMonth };
    }
    return { top: `${start.getDate()}. ${startMonth}`, bottom: `– ${end.getDate()}. ${endMonth}` };
  }
  return { top: String(start.getDate()), bottom: startMonth };
}

/**
 * Ausführlicher Datums-String, IMMER deutsch formatiert (z.B. "Sa., 4. Juli 2026").
 * Das rohe `dateFormatted` vom Crawler ist englisch und wird nur als letzter
 * Fallback genutzt, wenn keine Timestamps vorhanden sind.
 */
export function formatDateRange(event: CalendarEvent): string {
  if (event.startDate == null) {
    return event.dateFormatted && event.dateFormatted.trim().length > 0
      ? event.dateFormatted
      : '–';
  }
  const opts: Intl.DateTimeFormatOptions = {
    weekday: 'short',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  };
  const startStr = new Date(event.startDate).toLocaleDateString('de-DE', opts);
  if (event.endDate == null || startOfDayTs(event.endDate) <= startOfDayTs(event.startDate)) {
    return startStr;
  }
  const endStr = new Date(event.endDate).toLocaleDateString('de-DE', opts);
  return `${startStr} – ${endStr}`;
}

/** Ort als String: Venue, Stadt, Land. */
export function formatLocation(event: CalendarEvent): string | null {
  const parts: string[] = [];
  if (event.venue) parts.push(event.venue);
  if (event.city) parts.push(event.city);
  if (event.country) parts.push(event.country);
  return parts.length > 0 ? parts.join(', ') : null;
}

/** Löst die (erste auffindbare) Kategorie eines Events auf. */
export function resolveCategory(
  event: CalendarEvent,
  categoryById: Map<string, CalendarCategory>,
): CalendarCategory | null {
  const ids = [...(event.categoryIds || []), event.categoryId].filter(Boolean) as string[];
  for (const id of ids) {
    const cat = categoryById.get(id);
    if (cat) return cat;
  }
  return null;
}

/** Prüft ob ein Event eine Kategorie besitzt. */
export function eventHasCategory(event: CalendarEvent, categoryId: string): boolean {
  if (event.categoryIds && event.categoryIds.includes(categoryId)) return true;
  return event.categoryId === categoryId;
}

/** Client-seitige Suche über Titel, Ort und Organisation. */
export function matchesSearch(event: CalendarEvent, term: string): boolean {
  const lower = term.toLowerCase();
  const haystack = [
    event.title,
    event.venue,
    event.city,
    event.country,
    event.organization,
    ...(event.tags || []),
  ]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();
  return haystack.includes(lower);
}
